/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call */
import type {
  AddToCalendarDelegate,
  CreateExternalUrlHandleOptions,
  DocumentDownloadDelegate,
  ExternalUrlDelegate,
  ExternalUrlOpenHandle,
  LifecycleDelegate,
  PlatformDelegate,
} from '@/features/shell/platform/types'
import {
  AddToCalendarAction,
  ExitAction,
  DownloadDocumentAction,
  AuthenticatedEvent,
  OpenExternalUrlAction,
  SessionExpiredEvent,
} from '@/features/shell/platform/messages'

class NativeExternalUrlDelegate implements ExternalUrlDelegate {
  createHandle(
    _options?: CreateExternalUrlHandleOptions,
  ): ExternalUrlOpenHandle {
    return {
      open(url: string) {
        sendNativeMessage(OpenExternalUrlAction(url))
      },
      abort() {
        // no-op
      },
    }
  }
}

class NativeDocumentDownloadDelegate implements DocumentDownloadDelegate {
  requiresFormSubmission = false

  download(url: string): void {
    sendNativeMessage(DownloadDocumentAction(url))
  }
}

class NativeAddToCalendarDelegate implements AddToCalendarDelegate {
  addToCalendar(icsFile: File): void {
    const reader = new FileReader()
    reader.onloadend = () =>
      sendNativeMessage(AddToCalendarAction(reader.result as string))

    reader.readAsDataURL(icsFile)
  }
}

class NativeLifecycleDelegate implements LifecycleDelegate {
  readonly supportsDelegatedExit = true

  notifyAuthenticated(): void {
    sendNativeMessage(AuthenticatedEvent())
  }

  notifySessionExpired(): void {
    sendNativeMessage(SessionExpiredEvent())
  }

  requestExit(): void {
    sendNativeMessage(ExitAction())
  }
}

/**
 * Platform delegate that allows interfacing with native hosts.
 * Falls back to the default delegate for non-registered handlers.
 */
export class NativePlatformDelegate implements PlatformDelegate {
  private _externalUrlDelegate = new NativeExternalUrlDelegate()
  private _documentDownloadDelegate = new NativeDocumentDownloadDelegate()
  private _addToCalendarDelegate = new NativeAddToCalendarDelegate()
  private _lifecycleDelegate = new NativeLifecycleDelegate()

  getExternalUrlDelegate() {
    return this._externalUrlDelegate
  }

  getDocumentDownloadDelegate() {
    return this._documentDownloadDelegate
  }

  getAddToCalendarDelegate() {
    return this._addToCalendarDelegate
  }

  getLifecycleDelegate() {
    return this._lifecycleDelegate
  }

  constructor() {
    this.getExternalUrlDelegate = this.getExternalUrlDelegate.bind(this)
    this.getDocumentDownloadDelegate =
      this.getDocumentDownloadDelegate.bind(this)
    this.getAddToCalendarDelegate = this.getAddToCalendarDelegate.bind(this)
    this.getLifecycleDelegate = this.getLifecycleDelegate.bind(this)
  }

  /**
   * Checks whether the native platform delegate is supported.
   */
  static get isSupported() {
    return isEmbeddedInNative()
  }
}

/**
 * Checks whether we are embedded in a native app.
 */
function isEmbeddedInNative() {
  return Boolean(
    // @ts-ignore
    window.ReactNativeWebView ||
    // @ts-ignore
    window.flutter_inappwebview ||
    // @ts-ignore
    window.webkit ||
    // @ts-ignore
    window.Android,
  )
}

/**
 * Sends a message from a web view environment to its native container app
 * (React Native, Flutter, iOS WebView, etc.) via the appropriate bridge.
 *
 * @param {object} data The message payload to send. Will be stringified if
 * needed.
 */
function sendNativeMessage(data: any) {
  const message = JSON.stringify(data)

  // React Native (iOS and Android)
  // @ts-ignore
  if (window.ReactNativeWebView) {
    // @ts-ignore
    window.ReactNativeWebView.postMessage(message)
    return
  }

  // Flutter InAppWebView (Android) - uses a slightly different API style
  // @ts-ignore
  if (window.flutter_inappwebview) {
    // @ts-ignore
    window.flutter_inappwebview.callHandler('taxfyleMessageHandler', message)
    return
  }

  // iOS WKWebView (Fallback method for pure native iOS)
  if (
    // @ts-ignore
    window.webkit?.messageHandlers?.nativeApp
  ) {
    // iOS WKWebView bridge typically expects an object, not a stringified JSON string
    // @ts-ignore
    window.webkit.messageHandlers.nativeApp.postMessage(data)
    return
  }

  // Android native
  // @ts-ignore
  if (window.Android?.postMessage) {
    // @ts-ignore
    window.Android.postMessage(message)
    return
  }

  // Standard Browser Window (Fallback for web-to-web communication)
  // If running in a browser without a native bridge, use standard window.postMessage
  // This assumes a parent window is listening.
  // We can just pass the raw `data` along without serializing it.
  window.parent.postMessage(data, '*')
}
